import store from '@/store';
import authentication from '@/authentication';
import products from '@/components/products';
import {
  buildProductDetailRoute,
  localeAwareCanonicalSlugForDetail,
  toProductSlug,
} from '@/components/products/product.util';
import { resolveActivePkLanguage } from '@/configured/resolve-active-language';
import Products from './Products.vue';
import ProductDetail from './ProductDetail.vue';

const parent = { template: '<router-view></router-view>' };

async function guardProducts(to, from, next) {
  // redirect to the first category if none is provided
  if (!to.params.category) {
    // Vuex simply does not want to allow us to call the getFilteredCategories
    // getter here. Since we're dealing with top level categories, we can cheat
    // and use the unfiltered categories.
    const productCategories =
      store.state[products.store.namespace].internalProductCategories;
    const firstCategorySlug =
      productCategories && productCategories.length > 0
        ? productCategories[0].slug
        : null;

    if (firstCategorySlug) {
      next({
        name: 'Products',
        params: {
          category: firstCategorySlug,
        },
      });
    } else {
      next();
    }
  } else {
    // Handle redirects from the old products deep links
    const possibleProductId = parseInt(to.params.category);
    if (isNaN(possibleProductId)) {
      // Handle old products link
      if ('all' == to.params.category) {
        next({ name: 'Products' });
      } else {
        next();
      }
    } else {
      const productById =
        store.getters[`${products.store.namespace}/getProductById`](
          possibleProductId
        );
      next(buildProductDetailRoute(productById || { id: possibleProductId }));
    }
  }
}

async function guardProductDetail(to, from, next) {
  /** Matches ProductDetail / GraphQL: bucket + slug follow resolved PK language (see resolve-active-language). */
  const currentLanguage = resolveActivePkLanguage();
  const requestedSlug =
    to.params.productSlug && String(to.params.productSlug).trim();

  const hotForLang =
    store.state.products.hotLoadedProductDetails &&
    store.state.products.hotLoadedProductDetails[currentLanguage];

  const needsFullLoad = !hotForLang || !hotForLang[to.params.id];

  async function runFullLoad() {
    if (!needsFullLoad) {
      return;
    }
    await store.commit(`${authentication.store.namespace}/SET_WORKING`, true);
    try {
      await store.dispatch(
        `${products.store.namespace}/loadIndividualProductDataById`,
        {
          id: to.params.id,
        },
      );
    } finally {
      store.commit(`${authentication.store.namespace}/SET_WORKING`, false);
    }
  }

  const [, canonicalFromPrimarySite] = await Promise.all([
    runFullLoad(),
    store.dispatch(
      `${products.store.namespace}/fetchCanonicalSlugForActiveLocale`,
      { id: to.params.id },
    ),
  ]);

  const hotLoadedEntry =
    store.state.products.hotLoadedProductDetails &&
    store.state.products.hotLoadedProductDetails[currentLanguage] &&
    store.state.products.hotLoadedProductDetails[currentLanguage][to.params.id];

  let canonicalSlug = localeAwareCanonicalSlugForDetail({
    language: currentLanguage,
    primarySiteSlug: canonicalFromPrimarySite,
    entry: hotLoadedEntry,
  });
  if (!canonicalSlug) {
    canonicalSlug = toProductSlug(hotLoadedEntry || {});
  }

  if (
    requestedSlug &&
    canonicalSlug &&
    requestedSlug.toLowerCase() !== canonicalSlug.toLowerCase()
  ) {
    next({
      ...buildProductDetailRoute({
        id: to.params.id,
        slug: canonicalSlug,
      }),
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return;
  }

  if (
    to.name === 'ProductDetailAlias' &&
    canonicalSlug &&
    canonicalSlug.length > 0
  ) {
    next({
      ...buildProductDetailRoute({
        id: to.params.id,
        slug: canonicalSlug,
      }),
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return;
  }

  next();
}

const routes = [
  {
    path: '/products/:category?',
    name: 'Products',
    component: Products,
    props: true,
    beforeEnter: guardProducts,
  },
  {
    path: '/product/:id(\\d+)-:productSlug',
    name: 'ProductDetail',
    component: ProductDetail,
    props: true,
    beforeEnter: guardProductDetail,
  },
  {
    path: '/product/:id(\\d+)',
    name: 'ProductDetailAlias',
    component: ProductDetail,
    props: true,
    beforeEnter: guardProductDetail,
  },
];

export default routes;
